import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRelatedArticles, getHotArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | undefined>();
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [hotArticles, setHotArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (id) {
      const found = getArticleById(id);
      setArticle(found);
      if (found) {
        setRelatedArticles(getRelatedArticles(found, 3));
      }
    }
    setHotArticles(getHotArticles().slice(0, 4));
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="animate-pulse space-y-6">
              <div className="h-6 w-64 bg-background-800 rounded"></div>
              <div className="h-10 w-full bg-background-800 rounded"></div>
              <div className="h-64 bg-background-800 rounded-xl"></div>
              <div className="space-y-3">
                {[1,2,3,4,5].map((i) => (
                  <div key={i} className="h-4 bg-background-800 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <i className="ri-file-unknow-line text-5xl text-foreground-600 mb-4"></i>
            <h2 className="text-2xl font-bold text-foreground-300 mb-2">文章不存在</h2>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-20 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: article.categoryName, path: `/category/${article.category}` },
            { label: article.title },
          ]} />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              {article.isExclusive && (
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500/20 text-accent-400 border border-accent-500/30">独家爆料</span>
              )}
              {article.isHot && (
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-500/20 text-primary-400 border border-primary-500/30">热门追踪</span>
              )}
              {category && (
                <Link to={`/category/${article.category}`} className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-800 text-foreground-400 hover:text-foreground-200 transition-colors">
                  {article.categoryName}
                </Link>
              )}
            </div>

            <h1 className="text-2xl md:text-4xl font-black text-foreground-50 leading-tight mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </header>

          <figure className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 黑网料`}
              className="w-full aspect-[16/9] object-cover"
            />
          </figure>

          <div
            className="prose prose-invert max-w-none mb-12
              prose-headings:font-bold prose-headings:text-foreground-100 prose-headings:mt-8 prose-headings:mb-4
              prose-h2:text-xl md:prose-h2:text-2xl
              prose-p:text-foreground-300 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
              prose-strong:text-foreground-200 prose-strong:font-semibold
              prose-a:text-primary-400 prose-a:no-underline hover:prose-a:text-primary-300
              prose-blockquote:border-primary-500 prose-blockquote:bg-background-900/50 prose-blockquote:rounded-r-lg prose-blockquote:py-3 prose-blockquote:px-5 prose-blockquote:text-foreground-400
              [&_h2]:font-heading"
            style={{ fontFamily: "var(--font-body)" }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-12 pt-6 border-t border-background-800/60">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1.5 rounded-lg text-xs bg-background-900 text-foreground-500 border border-background-800/60">
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between p-5 rounded-xl bg-background-900 border border-background-800/60 mb-12">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-user-line text-primary-400"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-200">{article.author}</p>
                <p className="text-xs text-foreground-600">黑网料特约调查员</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-600">分享：</span>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-800 text-foreground-500 hover:text-primary-400 hover:bg-primary-500/10 transition-all duration-200">
                <i className="ri-share-forward-line"></i>
              </button>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                <h3 className="text-xl font-bold text-foreground-100" style={{ fontFamily: "var(--font-heading)" }}>
                  相关推荐
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} variant="default" />
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground-100" style={{ fontFamily: "var(--font-heading)" }}>
                热门文章
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {hotArticles.map((hot) => (
                <ArticleCard key={hot.id} article={hot} variant="compact" />
              ))}
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}